import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import SymbolPriceChart from '@/components/ui/SymbolPriceChart';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import EditSwingTradeModal from '@/components/admin/recommendations/EditSwingTradeModal';
import EditSwingTradeAdjustementModal from '@/components/admin/recommendations/EditSwingTradeAdjustementModal';
import {
  faSpinner,
  faArrowLeft,
  faEdit,
  faTrash,
  faUpDown,
  faChartLine,
  faCalendarAlt,
  faCheck,
  faTimes,
  faBell,
  faGlobe,
  faList,
} from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { formatDate, formatCurrencyCentValue, formatCurrency, formatPercentage, formatDelta } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import { ADMIN_TRADING_ROUTES } from '@/constants/routes';
import type { RecommendationSwingtrade, SwingTradeID, SwingTradeAdjustement, SymbolHistoryEntry } from '@/models';
import type { GraphPoint } from '@/modelsAPI';

const pageSize = 50;

// Helper to format seconds as time HH:MM
function formatSecondsAsTime(seconds: number | null): string {
  if (seconds === null) return '-';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Helper to get state display info
function getStateInfo(state: string): { label: string; colorClass: string } {
  switch (state) {
    case 'OPEN':
      return { label: 'Offen', colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' };
    case 'FINISHED_PROFIT':
      return { label: 'Erfolgreich beendet', colorClass: 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-200' };
    case 'FINISHED_LOSS':
      return { label: 'Beendet (Verlust)', colorClass: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' };
    default:
      return { label: state, colorClass: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' };
  }
}

// Helper to get type display info
function getTypeInfo(type: string): { label: string; colorClass: string } {
  switch (type) {
    case 'LONG':
      return { label: 'Long', colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' };
    case 'SHORT':
      return { label: 'Short', colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' };
    default:
      return { label: type, colorClass: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' };
  }
}

export default function AdminSwingTradeFullDetail() {
  const { isAdmin } = useUser();
  const { recommendationId } = useParams<{ recommendationId: string }>();
  const [recommendation, setRecommendation] = useState<RecommendationSwingtrade | null>(null);
  const [graphPoints, setGraphPoints] = useState<GraphPoint[]>([]);
  const [history, setHistory] = useState<SymbolHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState<SwingTradeAdjustement | null>(null);
  const [isEditAdjustmentModalOpen, setIsEditAdjustmentModalOpen] = useState(false);
  const [deletingAdjustmentId, setDeletingAdjustmentId] = useState<string | null>(null);

  useEffect(() => {
    if (recommendationId) {
      fetchRecommendationDetails();
    }
  }, [recommendationId]);

  const fetchChartGraph = async (rec: RecommendationSwingtrade) => {
    if (!rec.symbol?.id) return;

    try {
      setGraphLoading(true);
      // Use startTimestamp and endTimestamp (or current time if still open) for the chart range
      const start = new Date(rec.startTimestamp).toISOString();
      const end = rec.endTimestamp !== null ? new Date(rec.endTimestamp).toISOString() : new Date().toISOString();

      const chartResponse = await APIRoutes.getSymbolGraph(rec.symbol.id, {
        start,
        end,
        points: PAGINATION.CHART.DETAIL_POINTS
      });
      setGraphPoints(chartResponse.points);
    } catch (err) {
      console.error('Error loading chart graph:', err);
      handleAPIError(err);
    } finally {
      setGraphLoading(false);
    }
  };

  const fetchSymbolHistory = async (rec: RecommendationSwingtrade, page: number = 1) => {
    if (!rec.symbol?.id) return;

    try {
      setHistoryLoading(true);
      const cursorToken = page > 1 ? `$${page}` : undefined;

      // Use startTimestamp and endTimestamp for filtering
      const timestampAfter = new Date(rec.startTimestamp).toISOString();
      const timestampBefore = rec.endTimestamp !== null ? new Date(rec.endTimestamp).toISOString() : new Date().toISOString();

      const historyResponse = await APIRoutes.listSymbolHistory(rec.symbol.id, {
        limit: pageSize,
        next: cursorToken,
        timestampAfter,
        timestampBefore,
        sort: 'timestamp',
        sortDirection: 'ASC'
      });

      setHistory(historyResponse.history);
      setHistoryTotal(historyResponse.total);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error loading symbol history:', err);
      handleAPIError(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchRecommendationDetails = async () => {
    if (!recommendationId) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const recResponse = await APIRoutes.getSwingTrade(recommendationId as SwingTradeID);
      setRecommendation(recResponse);

      // Fetch chart and history in parallel
      await Promise.all([
        fetchChartGraph(recResponse),
        fetchSymbolHistory(recResponse)
      ]);
    } catch (err) {
      console.error('Critical error loading recommendation details:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Swing-Trade-Details');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendationUpdated = (updatedRecommendation: RecommendationSwingtrade) => {
    setRecommendation(updatedRecommendation);
  };

  const handleDeleteAdjustment = async (adjustment: SwingTradeAdjustement) => {
    if (recommendation === null) return;

    if (recommendation.adjustements.length === 1) {
      handleAPIError(
        new Error('Der letzte Einstieg kann nicht gelöscht werden'),
        'Fehler beim Löschen'
      );
      return;
    }

    const currencyForConfirm = recommendation.symbol?.currentData?.currency ?? 'USD';
    const confirmMessage =
      `Einstieg wirklich löschen?\n\n` +
      `% des Depots: ${formatDelta(adjustment.delta)}\n` +
      `Preis: ${formatCurrencyCentValue(adjustment.price, currencyForConfirm)}\n` +
      `Zeitpunkt: ${formatDate(adjustment.timestamp)}\n\n` +
      `Diese Aktion kann nicht rückgängig gemacht werden.`;

    if (!window.confirm(confirmMessage)) return;

    setDeletingAdjustmentId(adjustment.id);
    try {
      const updatedTrade = await APIRoutes.deleteSwingTradeAdjustement(recommendation.id, adjustment.id);
      setRecommendation(updatedTrade);
      showSuccessToast('Einstieg erfolgreich gelöscht');
    } catch (err) {
      handleAPIError(err, 'Fehler beim Löschen des Einstiegs');
    } finally {
      setDeletingAdjustmentId(null);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Swing-Trade nicht verfügbar"
        message={error ?? 'Die Swing-Trade-Details konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchRecommendationDetails}
        onBack={() => window.history.back()}
      />
    );
  }

  if (loading || recommendation === null) {
    return (
      <div className="flex justify-center items-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
      </div>
    );
  }

  const stateInfo = getStateInfo(recommendation.state);
  const typeInfo = getTypeInfo(recommendation.type);
  const currency = recommendation.symbol?.currentData?.currency ?? 'USD';

  // Sort adjustments by timestamp (newest first)
  const sortedAdjustments = [...recommendation.adjustements].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to={ADMIN_TRADING_ROUTES.SWING_LIST}
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 dark:bg-purple-900/50 p-2 rounded-lg">
              <FontAwesomeIcon icon={faUpDown} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Admin Ansicht: {recommendation.symbolTicker}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Swing-Trade ID: {recommendation.id}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsEditModalOpen(true)}
            disabled={!isAdmin}
            className={`px-4 py-2 bg-gray-600 text-white rounded-lg flex items-center space-x-2 transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
            <span>Bearbeiten</span>
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recommendation Data Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faUpDown} className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
            Empfehlungs-Daten
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{recommendation.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Symbol Ticker</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold">{recommendation.symbolTicker}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Handelsrichtung</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.colorClass}`}>
                    {typeInfo.label}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateInfo.colorClass}`}>
                    {stateInfo.label}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Take-Profit</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {recommendation.takeProfit > 0 ? formatCurrencyCentValue(recommendation.takeProfit, currency) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktuelle Position</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatPercentage(recommendation.currentPosition)}</dd>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Berechnete Felder</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Einstiegspreis</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.initialPrice, currency)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktueller Preis</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.currentPrice, currency)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ø-Preis (gesamt)</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.averagePrice, currency)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ø-Kaufpreis</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.averageBuyPrice, currency)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ø-Verkaufspreis</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.averageSellPrice, currency)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ø-Ausstiegspreis</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {recommendation.averageExitPrice !== null ? formatCurrencyCentValue(recommendation.averageExitPrice, currency) : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Win/Loss %</dt>
                  <dd className={`mt-1 text-sm font-semibold ${recommendation.winLossPercentage >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {recommendation.winLossPercentage >= 0 ? '+' : ''}{(recommendation.winLossPercentage * 100).toFixed(2)}%
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Rendite</dt>
                  <dd className={`mt-1 text-sm font-semibold ${recommendation.yield >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {recommendation.yield >= 0 ? '+' : ''}{(recommendation.yield * 100).toFixed(2)}%
                  </dd>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Timestamps</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Startdatum</dt>
                  <dd className="text-gray-900 dark:text-white">{formatDate(recommendation.startTimestamp)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Enddatum</dt>
                  <dd className="text-gray-900 dark:text-white">{recommendation.endTimestamp !== null ? formatDate(recommendation.endTimestamp) : '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Letztes Adjustment</dt>
                  <dd className="text-gray-900 dark:text-white">{formatDate(recommendation.lastAdjustement)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Erstellt</dt>
                  <dd className="text-gray-900 dark:text-white">{formatDate(recommendation.created)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Aktualisiert</dt>
                  <dd className="text-gray-900 dark:text-white">{recommendation.updated !== null ? formatDate(recommendation.updated) : '-'}</dd>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Flags</h3>
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.boost ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Boost: {recommendation.boost ? 'Ja' : 'Nein'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.free ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Free: {recommendation.free ? 'Ja' : 'Nein'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.onlyCommunityMember ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Nur Community: {recommendation.onlyCommunityMember ? 'Ja' : 'Nein'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.excludeCommunityMember ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Community ausschließen: {recommendation.excludeCommunityMember ? 'Ja' : 'Nein'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Flags Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faBell} className="w-4 h-4 mr-2 text-yellow-600 dark:text-yellow-400" />
            Benachrichtigungs-Status
          </h2>

          <div className="space-y-3">
            <NotificationFlag label="Discord Benachrichtigung" sent={recommendation.discordNotificationSent} />
            <NotificationFlag label="SCN Benachrichtigung" sent={recommendation.scnNotificationSent} />
            <NotificationFlag label="Push Benachrichtigung" sent={recommendation.pushNotificationSent} />
            <NotificationFlag label="Telegram Benachrichtigung" sent={recommendation.telegramNotificationSent} />
            <NotificationFlag label="Mail an Free-User" sent={recommendation.freeUserMailNotificationSent} />
            <NotificationFlag label="Mail an alle User" sent={recommendation.allUsersMailNotificationSent} />
            <NotificationFlag label="Discord Kommentar" sent={recommendation.discordNotificationCommentSent} />
            <NotificationFlag label="Telegram Kommentar" sent={recommendation.telegramNotificationCommentSent} />
            <NotificationFlag label="Mail Kommentar" sent={recommendation.mailNotificationCommentSent} />
            <NotificationFlag label="SCN Kommentar" sent={recommendation.scnNotificationCommentSent} />
            <NotificationFlag label="Push Kommentar" sent={recommendation.pushNotificationCommentSent} />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kommentar</h3>
            {recommendation.notificationComment !== '' ? (
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                {recommendation.notificationComment}
              </p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">Kein Kommentar</p>
            )}
          </div>
        </div>
      </div>

      {/* Adjustments Section */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <FontAwesomeIcon icon={faList} className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
          Adjustments ({recommendation.adjustements.length})
        </h2>

        {recommendation.adjustements.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">Keine Adjustments vorhanden</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Zeitstempel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    % des Depots
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Preis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Laufende Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Benachrichtigungen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kommentar
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedAdjustments.map((adjustment, index) => {
                  // Calculate running position (sum from current to end since sorted newest-first)
                  const runningPosition = sortedAdjustments
                    .slice(index)
                    .reduce((sum, adj) => sum + adj.delta, 0);
                  return (
                    <tr key={adjustment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {adjustment.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(adjustment.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${
                          adjustment.delta >= 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatDelta(adjustment.delta)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrencyCentValue(adjustment.price, currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {formatPercentage(runningPosition)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          <NotificationBadge label="Discord" sent={adjustment.discordNotificationSent ?? false} />
                          <NotificationBadge label="Telegram" sent={adjustment.telegramNotificationSent ?? false} />
                          <NotificationBadge label="SCN" sent={adjustment.scnNotificationSent ?? false} />
                          <NotificationBadge label="Push" sent={adjustment.pushNotificationSent ?? false} />
                          <NotificationBadge label="Mail" sent={adjustment.mailNotificationSent ?? false} />
                          <NotificationBadge label="DC-Kom" sent={adjustment.discordNotificationCommentSent ?? false} />
                          <NotificationBadge label="TG-Kom" sent={adjustment.telegramNotificationCommentSent ?? false} />
                          <NotificationBadge label="M-Kom" sent={adjustment.mailNotificationCommentSent ?? false} />
                          <NotificationBadge label="SCN-Kom" sent={adjustment.scnNotificationCommentSent ?? false} />
                          <NotificationBadge label="Push-Kom" sent={adjustment.pushNotificationCommentSent ?? false} />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs whitespace-pre-wrap break-words">
                        {adjustment.notificationComment !== '' ? adjustment.notificationComment : <span className="text-gray-400 dark:text-gray-500 italic">-</span>}
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                          <button
                            onClick={() => {
                              setEditingAdjustment(adjustment);
                              setIsEditAdjustmentModalOpen(true);
                            }}
                            disabled={deletingAdjustmentId === adjustment.id}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer disabled:opacity-50"
                            title="Bearbeiten"
                          >
                            <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAdjustment(adjustment)}
                            disabled={deletingAdjustmentId === adjustment.id || recommendation.adjustements.length === 1}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            title={recommendation.adjustements.length === 1 ? 'Letzter Einstieg kann nicht gelöscht werden' : 'Löschen'}
                          >
                            {deletingAdjustmentId === adjustment.id ? (
                              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Symbol & Exchange Data Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Symbol Data Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faChartLine} className="w-4 h-4 mr-2 text-cyan-600 dark:text-cyan-400" />
            Symbol-Daten
          </h2>

          {recommendation.symbol ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ticker</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold">{recommendation.symbol.ticker}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.symbol.name ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Branche</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.symbol.sector ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Im Ticker anzeigen</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.symbol.showInTicker ? 'Ja' : 'Nein'}</dd>
                </div>
              </div>

              {recommendation.symbol.currentData && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Aktuelle Marktdaten</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <StockDataRow label="Kurs" value={formatCurrency(recommendation.symbol.currentData.price, currency)} />
                      <StockDataRow label="Eröffnungskurs" value={formatCurrency(recommendation.symbol.currentData.priceOpen, currency)} />
                      <StockDataRow label="Tageshoch" value={formatCurrency(recommendation.symbol.currentData.high, currency)} />
                      <StockDataRow label="Tagestief" value={formatCurrency(recommendation.symbol.currentData.low, currency)} />
                      <StockDataRow label="52W Hoch" value={formatCurrency(recommendation.symbol.currentData.high52, currency)} />
                      <StockDataRow label="52W Tief" value={formatCurrency(recommendation.symbol.currentData.low52, currency)} />
                      <StockDataRow label="Änderung" value={recommendation.symbol.currentData.change !== null ? `${recommendation.symbol.currentData.change >= 0 ? '+' : ''}${recommendation.symbol.currentData.change.toFixed(2)}` : '-'} />
                      <StockDataRow label="Änderung %" value={recommendation.symbol.currentData.changePct !== null ? `${recommendation.symbol.currentData.changePct.toFixed(2)}%` : '-'} />
                      <StockDataRow label="Vortagesschluss" value={formatCurrency(recommendation.symbol.currentData.closeYest, currency)} />
                      <StockDataRow label="Volumen" value={recommendation.symbol.currentData.volume?.toLocaleString('de-DE') ?? '-'} />
                      <StockDataRow label="Ø Volumen" value={recommendation.symbol.currentData.volumeAvg?.toLocaleString('de-DE') ?? '-'} />
                      <StockDataRow label="Marktkapitalisierung" value={formatCurrency(recommendation.symbol.currentData.marketCap, currency)} />
                      <StockDataRow label="KGV (P/E)" value={recommendation.symbol.currentData.pe?.toFixed(2) ?? '-'} />
                      <StockDataRow label="EPS" value={formatCurrency(recommendation.symbol.currentData.eps, currency)} />
                      <StockDataRow label="Beta" value={recommendation.symbol.currentData.beta?.toFixed(2) ?? '-'} />
                      <StockDataRow label="Aktien im Umlauf" value={recommendation.symbol.currentData.shares?.toLocaleString('de-DE') ?? '-'} />
                      <StockDataRow label="Währung" value={currency} />
                      <StockDataRow label="Datenverzögerung" value={recommendation.symbol.currentData.dataDelay !== null ? `${recommendation.symbol.currentData.dataDelay} Min.` : '-'} />
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Keine Symbol-Daten verfügbar</p>
          )}
        </div>

        {/* Exchange Data Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faGlobe} className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
            Börsen-Daten
          </h2>

          {recommendation.stockExchange ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{recommendation.stockExchange.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Kennung</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold">{recommendation.stockExchange.identifier}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.stockExchange.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Zeitzone</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.stockExchange.timezone}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Öffnungszeit</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatSecondsAsTime(recommendation.stockExchange.openingTime)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Schließzeit</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatSecondsAsTime(recommendation.stockExchange.closingTime)}</dd>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Erstellt</dt>
                    <dd className="text-gray-900 dark:text-white">{formatDate(recommendation.stockExchange.created)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Aktualisiert</dt>
                    <dd className="text-gray-900 dark:text-white">{recommendation.stockExchange.updated !== null ? formatDate(recommendation.stockExchange.updated) : '-'}</dd>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Keine Börsen-Daten verfügbar</p>
          )}
        </div>
      </div>

      {/* Price Chart Section */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-8">
        <div className="flex items-center mb-6">
          <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Kursverlauf ({formatDate(recommendation.startTimestamp)} - {recommendation.endTimestamp !== null ? formatDate(recommendation.endTimestamp) : 'jetzt'})
          </h2>
          {graphLoading && (
            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin ml-2" />
          )}
        </div>
        <SymbolPriceChart
          points={graphPoints}
          currency={currency}
          height={400}
          strikePrice={recommendation.averagePrice / 100}
        />
      </div>

      {/* Symbol History Table Section */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
        <div className="flex items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <FontAwesomeIcon icon={faCalendarAlt} className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Preis-Historie ({historyTotal})
          </h2>
          {historyLoading && (
            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin ml-2" />
          )}
        </div>

        {history.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faCalendarAlt} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Keine Historie verfügbar
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Für den Zeitraum dieses Trades sind keine historischen Daten vorhanden.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aufgezeichnet am
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Kurs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Änderung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Volumen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Marktkapitalisierung
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {history.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(entry.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(entry.data.price, entry.data.currency ?? 'USD')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${(entry.data.change ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {entry.data.change !== null && entry.data.changePct !== null ? `${entry.data.change >= 0 ? '+' : ''}${entry.data.change.toFixed(2)} (${entry.data.changePct.toFixed(2)}%)` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {entry.data.volume?.toLocaleString('de-DE') ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(entry.data.marketCap, entry.data.currency ?? 'USD')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={historyTotal}
              pageSize={pageSize}
              onPageChange={(page) => fetchSymbolHistory(recommendation, page)}
              loading={historyLoading}
            />
          </>
        )}
      </div>

      {/* Edit Modal */}
      <EditSwingTradeModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        swingTrade={recommendation}
        onSwingTradeUpdated={handleRecommendationUpdated}
      />

      {/* Edit Adjustment Modal */}
      <EditSwingTradeAdjustementModal
        isOpen={isEditAdjustmentModalOpen}
        onClose={() => {
          setIsEditAdjustmentModalOpen(false);
          setEditingAdjustment(null);
        }}
        swingTrade={recommendation}
        adjustment={editingAdjustment}
        onAdjustmentUpdated={handleRecommendationUpdated}
      />
    </>
  );
}

// Helper component for notification flag display
function NotificationFlag({ label, sent }: { label: string; sent: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <span className={`inline-flex items-center ${sent ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
        <FontAwesomeIcon icon={sent ? faCheck : faTimes} className="w-4 h-4" />
        <span className="ml-1 text-xs font-medium">{sent ? 'Gesendet' : 'Nicht gesendet'}</span>
      </span>
    </div>
  );
}

// Helper component for stock data rows
function StockDataRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-gray-900 dark:text-white">{value}</dd>
    </div>
  );
}

// Small notification badge for adjustment table
function NotificationBadge({ label, sent }: { label: string; sent: boolean }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
      sent
        ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    }`}>
      {label}
    </span>
  );
}
